import { useState, useEffect, useEffectEvent, useMemo, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { format, parseISO, formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Building2,
  Mail,
  Phone,
  MapPin,
  FileText,
  MessageCircle,
  Briefcase,
  Clock,
  User,
  Receipt,
  Wallet,
  CheckCircle2,
  ArrowRight,
  Calendar,
  Download,
  Loader2,
  Inbox,
  RefreshCw,
  Pencil,
  ListTodo,
} from 'lucide-react'
import { useCliente } from '@/hooks/useClientes'
import { useServiciosDelCliente } from '@/hooks/useServicios'
import { formatCurrency, formatDate, generarEnlaceWhatsApp } from '@/lib/utils'
import type { VistaSaldoCliente, ActividadEconomica, TareaCompleta } from '@/types/database'
import { ImpuestoBadge } from '@/components/ui/impuesto-badge'
import { useImpuestosCliente } from '@/hooks/useImpuestosCliente'
import { useVencimientosCliente } from '@/hooks/useCalendario'
import { ESTADOS_VENCIMIENTO_FISCAL } from '@/types/vencimientos'
import { useDescargarVencimientosPDF } from '@/hooks/useDescargarVencimientosPDF'
import { RepartoConceptos } from '@/components/clientes/reparto/RepartoConceptos'
import { EtiquetasClienteCard } from '@/components/clientes/EtiquetasClienteCard'
import { PromoInstagramClienteCard } from '@/components/clientes/PromoInstagramClienteCard'
import { labelMotivoBaja } from '@/components/clientes/forms/motivoBajaSchema'
import { useCasilleroResumen } from '@/hooks/useCasilleroElectronico'
import {
  useSincronizarPadronCliente,
  padronToDomicilioPatch,
  padronToFacetaPatch,
} from '@/hooks/useSincronizarPadronCliente'
import { useConsultarCuitAFIP } from '@/hooks/useConsultarCuitAFIP'
import { usePermissions } from '@/hooks/usePermissions'
import { puedeTocarTarea } from '@/lib/permisos-guard'
import type { ColorImpuesto } from '@/types/impuestos'
import { CopyableCell } from '@/components/ui/copyable-cell'
import { TareasLista, TareaDetalleDialog } from '@/core/components/tareas'
import { CajaFuertePanel } from './CajaFuertePanel'
import { DelegacionARCABanner, type EstadoDelegacion } from './DelegacionARCABanner'
import { AbrirAfipDirectoButton } from './AbrirAfipDirectoButton'
import { CuentaCorrienteTab } from './CuentaCorrienteTab'

/**
 * Mapea errores comunes de AFIP/casillero a mensajes user-friendly.
 */
function mapearErrorAFIP(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err ?? '')
  const lower = msg.toLowerCase()
  if (lower.includes('no constituido') || lower.includes('sin dfe') || lower.includes('domicilio fiscal electr')) {
    return 'Cliente sin Domicilio Fiscal Electrónico constituido en ARCA.'
  }
  if (lower.includes('no autorizado') || lower.includes('403')) {
    return 'No tenés autorización para consultar este casillero.'
  }
  if (lower.includes('401') || lower.includes('token') || lower.includes('jwt')) {
    return 'Sesión expirada. Recargá la página e intentá de nuevo.'
  }
  if (lower.includes('timeout') || lower.includes('500') || lower.includes('503') || lower.includes('no disponible')) {
    return 'AFIP no disponible. Reintentá en unos minutos.'
  }
  return `Error consultando casillero: ${msg}`
}

interface DiffRow {
  field: string
  label: string
  before: string | null
  after: string | null
}

const DIFF_LABELS: Record<string, string> = {
  domicilio_calle: 'Calle',
  domicilio_numero: 'Número',
  domicilio_piso: 'Piso',
  domicilio_depto: 'Depto',
  domicilio_localidad: 'Localidad',
  domicilio_provincia: 'Provincia',
  domicilio_cp: 'CP',
  domicilio_pais: 'País',
}

/**
 * ORR-233: los campos fiscales viven en `cliente_faceta_contable`, no en `personas`,
 * así que el diff los lee de otra fuente. Las etiquetas van aparte para poder
 * recorrer cada mapa contra su objeto.
 */
const DIFF_LABELS_FACETA: Record<string, string> = {
  categoria_monotributo: 'Categoría monotributo',
  actividad_principal: 'Actividad principal',
  codigo_actividad_afip: 'Código de actividad',
}

interface ClienteDetalleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  cliente: VistaSaldoCliente | null
  onEdit?: (cliente: VistaSaldoCliente) => void
  /** Tab inicial al abrir (default 'resumen'). Permite linkear directo a una sección. */
  initialTab?: string
  /** Eleva al padre la acción de enviar estado de cuenta desde la ficha (ORR-141). */
  onEnviarEstadoCuenta?: (cliente: VistaSaldoCliente) => void
  /** ORR-327: alta de tarea con este cliente ya asignado. */
  onCargarTarea?: (cliente: VistaSaldoCliente) => void
}

// Factores para calcular equivalente mensual
const FACTORES_FRECUENCIA: Record<string, number> = {
  mensual: 1,
  bimestral: 0.5,
  trimestral: 1 / 3,
  semestral: 1 / 6,
  anual: 1 / 12,
}

// Estados de un vencimiento cuyo trámite ya se cerró. La solapa los oculta hasta que se piden.
const ESTADOS_VENCIMIENTO_CERRADOS = new Set(['presentado', 'pagado', 'plan_pago'])

function calcularEquivalenteMensual(precio: number, frecuencia: string): number {
  return precio * (FACTORES_FRECUENCIA[frecuencia] || 1)
}

function formatFrecuencia(frecuencia: string): string {
  const labels: Record<string, string> = {
    mensual: 'Mensual',
    bimestral: 'Bimestral',
    trimestral: 'Trimestral',
    semestral: 'Semestral',
    anual: 'Anual',
  }
  return labels[frecuencia] || frecuencia
}

export function ClienteDetalleDialog({
  open,
  onOpenChange,
  cliente,
  onEdit,
  initialTab,
  onEnviarEstadoCuenta,
  onCargarTarea,
}: ClienteDetalleDialogProps) {
  const [activeTab, setActiveTab] = useState(initialTab ?? 'resumen')
  const { data: clienteCompleto, isLoading } = useCliente(cliente?.id || '')
  const { data: serviciosConPrecio = [] } = useServiciosDelCliente(cliente?.id || '')
  const { data: impuestosCliente = [] } = useImpuestosCliente(cliente?.id ?? undefined)
  const { data: vencimientosCliente = [], isLoading: loadingVencimientos } = useVencimientosCliente(cliente?.id || '')
  const { descargar: descargarPDF, generando: generandoPDF } = useDescargarVencimientosPDF()

  const { puede, emailUsuario } = usePermissions()
  // La lista de un cliente trae tareas de todo el equipo, asi que el cambio de estado
  // se gatea por fila: con solo `editarTareaPropia` la RPC rechaza las ajenas. La lista
  // y el detalle comparten este calculo — si divergen, el detalle ofrece lo que la fila
  // niega.
  // ORR-381: la regla es una sola y vive en `puedeTocarTarea`. Acá estaba copiada.
  const puedeCambiarEstadoTarea = useCallback(
    (tarea: TareaCompleta) => puedeTocarTarea(tarea, puede, emailUsuario),
    [puede, emailUsuario]
  )
  const cuitString = cliente?.cuit != null ? String(cliente.cuit) : null
  const casilleroResumen = useCasilleroResumen(cuitString)
  const personaId = clienteCompleto?.persona?.id ?? ''
  // ORR-233: el clienteId habilita la escritura de los campos fiscales, que viven
  // en `cliente_faceta_contable` y no en `personas`.
  const sincronizarPadron = useSincronizarPadronCliente(personaId, cliente?.id ?? undefined)
  const { consultarCuitAsync, isLoading: previewLoading } = useConsultarCuitAFIP()

  // AlertDialog state — diff Antes/Después
  // ORR-327: la tarea abierta desde la solapa. El detalle se monta fuera del
  // SheetContent, como el AlertDialog de AFIP de mas abajo.
  const [tareaDetalle, setTareaDetalle] = useState<TareaCompleta | null>(null)
  const [verTareasCompletadas, setVerTareasCompletadas] = useState(false)
  const [verVencimientosCerrados, setVerVencimientosCerrados] = useState(false)
  const [diffDialogOpen, setDiffDialogOpen] = useState(false)
  const [diffRows, setDiffRows] = useState<DiffRow[]>([])

  // Hooks DEBEN ir antes del early return (React rules of hooks)
  const persona = clienteCompleto?.persona
  const tieneDomicilioCargado = useMemo(
    () =>
      Boolean(
        persona?.domicilio_calle ||
          persona?.domicilio_localidad ||
          persona?.domicilio_provincia ||
          persona?.domicilio_cp,
      ),
    [persona?.domicilio_calle, persona?.domicilio_localidad, persona?.domicilio_provincia, persona?.domicilio_cp],
  )

  // Al abrir, vuelve a la solapa inicial y oculta las tareas completadas y los vencimientos cerrados.
  const resetAlAbrir = useEffectEvent(() => {
    setActiveTab(initialTab ?? 'resumen')
    setVerTareasCompletadas(false)
    setVerVencimientosCerrados(false)
  })
  useEffect(() => {
    if (open) resetAlAbrir()
  }, [open])

  if (!cliente) return null

  // Una solapa que el permiso oculta no se abre, ni desde initialTab ni desde la URL: cae en Resumen.
  const solapaPermitida: Record<string, boolean> = {
    cuenta: puede.verMovimientos,
    servicios: puede.verHonorariosCliente,
    tareas: puede.verTareas,
  }
  const solapaVisible = solapaPermitida[activeTab] === false ? 'resumen' : activeTab

  const estadoLower = cliente.estado?.toLowerCase()
  const esActivo = estadoLower === 'activo'
  // faceta_contable viene como array desde la API, tomamos el primer elemento
  const facetaRaw = clienteCompleto?.faceta_contable
  const faceta = Array.isArray(facetaRaw) ? facetaRaw[0] : facetaRaw

  /**
   * ORR-233: qué hay cargado que la sincronización pueda pisar. Antes alcanzaba con
   * mirar el domicilio; ahora el botón también escribe categoría y actividades, así
   * que un cliente sin domicilio pero con categoría cargada tiene que pasar por la
   * confirmación igual. `tieneDomicilioCargado` sigue existiendo: gobierna qué se
   * muestra en la solapa, que es otra pregunta.
   */
  const tieneDatosCargados =
    tieneDomicilioCargado ||
    Boolean(faceta?.categoria_monotributo || faceta?.actividad_principal || faceta?.codigo_actividad_afip)

  // Formatear domicilio
  const domicilioCalle = [
    persona?.domicilio_calle,
    persona?.domicilio_numero,
    persona?.domicilio_piso && `Piso ${persona.domicilio_piso}`,
    persona?.domicilio_depto && `Depto ${persona.domicilio_depto}`,
  ].filter(Boolean).join(' ')

  const domicilioLinea2 = [
    persona?.domicilio_cp && `CP ${persona.domicilio_cp}`,
    persona?.domicilio_localidad,
  ].filter(Boolean).join(' · ')

  const domicilioLinea3 = [
    persona?.domicilio_barrio && `Barrio ${persona.domicilio_barrio}`,
    persona?.domicilio_referencia,
  ].filter(Boolean).join(' · ')

  const tieneDomicilio = domicilioCalle || domicilioLinea2 || domicilioLinea3 || persona?.domicilio_provincia

  /**
   * Handler para "Sincronizar desde AFIP".
   * - Si no hay nada cargado que pisar → sincroniza directo (sin fricción).
   * - Si hay datos cargados → preview del diff y abre AlertDialog.
   *
   * ORR-233: el diff recorre dos objetos distintos —domicilio contra `personas`,
   * fiscales contra `cliente_faceta_contable`— porque viven en tablas separadas.
   */
  const handleSincronizar = async () => {
    if (!cuitString) return
    if (!tieneDatosCargados) {
      sincronizarPadron.mutate(cuitString)
      return
    }
    // Preview: traer padrón sin persistir, calcular diff
    try {
      const data = await consultarCuitAsync(cuitString)
      if (!data.encontrado || !data.persona) return

      const armarFilas = (
        labels: Record<string, string>,
        actual: Record<string, unknown> | null | undefined,
        after: Record<string, unknown>
      ): DiffRow[] =>
        Object.keys(labels).map((field) => {
          const beforeVal = actual?.[field] ?? null
          const afterVal = after[field] ?? null
          return {
            field,
            label: labels[field],
            before: beforeVal == null ? null : String(beforeVal),
            after: afterVal == null ? null : String(afterVal),
          }
        })

      const facetaAfter = padronToFacetaPatch(data.persona)

      const rows: DiffRow[] = [
        ...armarFilas(DIFF_LABELS, persona as Record<string, unknown> | null | undefined, { ...padronToDomicilioPatch(data.persona) }),
        // Un campo que ARCA no devolvió no viene en el patch, y no se escribe. Mostrarlo
        // como "→ vacío" diría que se va a borrar, que es justo lo contrario.
        ...armarFilas(DIFF_LABELS_FACETA, faceta as Record<string, unknown> | null | undefined, facetaAfter)
          .filter((r) => r.field in facetaAfter),
      ].filter((r) => r.before !== r.after)

      // Las actividades económicas son un array: comparar strings no dice nada útil,
      // pero omitirlas escondería un cambio que igual se aplica.
      const actividadesAfter = facetaAfter.actividades_economicas as unknown[] | undefined
      const actividadesActuales = (faceta?.actividades_economicas as unknown[] | null) || []
      if (actividadesAfter && actividadesAfter.length !== actividadesActuales.length) {
        rows.push({
          field: 'actividades_economicas',
          label: 'Actividades económicas',
          before: `${actividadesActuales.length}`,
          after: `${actividadesAfter.length}`,
        })
      }

      setDiffRows(rows)
      setDiffDialogOpen(true)
    } catch {
      // El error ya muestra toast desde useConsultarCuitAFIP
    }
  }

  const confirmarSincronizar = () => {
    if (!cuitString) return
    setDiffDialogOpen(false)
    sincronizarPadron.mutate(cuitString)
  }

  // Calcular total mensual de servicios
  const totalMensualServicios = serviciosConPrecio.reduce((sum, s) => {
    return sum + calcularEquivalenteMensual(s.precio, s.frecuencia || 'mensual')
  }, 0)

  // Actividades económicas
  const actividades = (faceta?.actividades_economicas as ActividadEconomica[] | null) || []

  // La solapa lista primero los vencidos y después los pendientes, cada grupo por fecha ascendente.
  // Los cerrados van al final, del más reciente al más viejo, y solo cuando se piden.
  type VencimientoFicha = (typeof vencimientosCliente)[number]
  const estadoVencimiento = (v: VencimientoFicha) => v.estado_efectivo || v.estado || 'pendiente'
  const rangoVencimiento = (v: VencimientoFicha) => (estadoVencimiento(v) === 'vencido' ? 0 : 1)
  const vencimientosAbiertos = vencimientosCliente
    .filter((v) => !ESTADOS_VENCIMIENTO_CERRADOS.has(estadoVencimiento(v)))
    .sort(
      (a, b) =>
        rangoVencimiento(a) - rangoVencimiento(b) ||
        (a.fecha_vencimiento ?? '').localeCompare(b.fecha_vencimiento ?? ''),
    )
  const vencimientosCerrados = vencimientosCliente
    .filter((v) => ESTADOS_VENCIMIENTO_CERRADOS.has(estadoVencimiento(v)))
    .sort((a, b) => (b.fecha_vencimiento ?? '').localeCompare(a.fecha_vencimiento ?? ''))
  const vencimientosVisibles = verVencimientosCerrados
    ? [...vencimientosAbiertos, ...vencimientosCerrados]
    : vencimientosAbiertos

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* ORR-350: 672px y no los 512 de `max-w-lg`. Con el ancho viejo entraban 6 de las 8
          solapas y las dos últimas —Venc. y Claves, la del PIN— quedaban detrás de un scroll
          sin indicador: nadie las encontraba. Las 8 piden 594px de fila. De paso la tabla de
          la solapa Tareas deja de desbordar, que es lo que ORR-348 no había podido cerrar. */}
      <SheetContent side="right" className="w-full sm:max-w-2xl p-0 flex flex-col">
        {/* Header mejorado */}
        <div className="bg-accent/50 border-b">
          <SheetHeader className="p-6 pb-4">
            <SheetTitle className="flex items-start gap-3">
              {/* Avatar */}
              <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center ring-2 ring-primary/30 shrink-0">
                <span className="text-xl font-semibold text-primary">
                  {cliente.nombre?.charAt(0)?.toUpperCase() || '?'}
                </span>
              </div>

              {/* Info principal */}
              <div className="flex-1 min-w-0">
                <span className="text-xl block truncate">{cliente.nombre}</span>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <Badge variant={esActivo ? 'default' : 'secondary'}>
                    {esActivo ? 'Activo' : 'Inactivo'}
                  </Badge>
                  {persona?.tipo && (
                    <span className="text-sm text-muted-foreground">
                      · {formatTipoPersona(persona.tipo)}
                    </span>
                  )}
                </div>
              </div>

              {/* Iconos de contacto rápido */}
              <div className="flex gap-1 shrink-0">
                {puede.crearTarea && onCargarTarea && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => onCargarTarea(cliente)}
                    title="Cargar tarea"
                    aria-label="Cargar tarea para este cliente"
                  >
                    <ListTodo className="h-4 w-4" />
                  </Button>
                )}
                {puede.editarCliente && onEdit && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => onEdit(cliente)}
                    title="Editar cliente"
                    aria-label="Editar cliente"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
                {cliente.email && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => window.open(`mailto:${cliente.email}`, '_blank')}
                    title="Enviar email"
                  >
                    <Mail className="h-4 w-4" />
                  </Button>
                )}
                {cliente.telefono && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-green-600"
                    onClick={() => {
                      const url = generarEnlaceWhatsApp(cliente.telefono)
                      if (url) window.open(url, '_blank')
                    }}
                    title="WhatsApp"
                  >
                    <MessageCircle className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </SheetTitle>
            <SheetDescription className="sr-only">
              Información detallada del cliente {cliente.nombre}
            </SheetDescription>
          </SheetHeader>
        </div>

        {/* Scroll nativo + overflow-x-hidden: evita el desborde horizontal del
            viewport display:table de Radix ScrollArea con el TabsList de 7 tabs (ORR-141). */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-20 bg-muted animate-pulse rounded-lg" />
              ))}
            </div>
          ) : (
            <Tabs value={solapaVisible} onValueChange={setActiveTab} className="w-full">
              <div className="mx-6 mt-4 overflow-x-auto" style={{ width: 'calc(100% - 3rem)' }}>
                <TabsList className="w-max min-w-full">
                  <TabsTrigger value="resumen">Resumen</TabsTrigger>
                  {puede.verMovimientos && <TabsTrigger value="cuenta">Cuenta</TabsTrigger>}
                  <TabsTrigger value="identidad">Identidad</TabsTrigger>
                  <TabsTrigger value="fiscal">Fiscal</TabsTrigger>
                  {puede.verHonorariosCliente && <TabsTrigger value="servicios">Servicios</TabsTrigger>}
                  {/* Tareas va sexta, no al final: en el ancho del Sheet entran 6 solapas
                      (463px de visible contra 450 acumulados a la sexta), así que puesta
                      octava quedaba detrás del scroll y no se encontraba. */}
                  {puede.verTareas && <TabsTrigger value="tareas">Tareas</TabsTrigger>}
                  <TabsTrigger value="vencimientos">Venc.</TabsTrigger>
                  <TabsTrigger value="claves">Claves</TabsTrigger>
                </TabsList>
              </div>

              {/* TAB RESUMEN */}
              <TabsContent value="resumen" className="px-6 py-4 space-y-6">
                {/* Métricas financieras (2 cards) */}
                <div className="grid grid-cols-2 gap-3">
                  {puede.verHonorariosCliente && (
                    <div className="rounded-lg bg-primary/5 border border-primary/20 p-4">
                      <div className="flex items-center gap-2 text-muted-foreground mb-1">
                        <Receipt className="h-4 w-4" />
                        <span className="text-xs uppercase tracking-wide">Honorarios</span>
                      </div>
                      <p className="text-xl font-bold text-primary">
                        {formatCurrency(totalMensualServicios)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">mensuales</p>
                    </div>
                  )}

                  {puede.verMovimientos && (
                  <div className={`rounded-lg border p-4 ${
                    (cliente.saldo ?? 0) > 0
                      ? 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800'
                      : 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800'
                  }`}>
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <Wallet className="h-4 w-4" />
                      <span className="text-xs uppercase tracking-wide">Saldo</span>
                    </div>
                    <p className={`text-xl font-bold ${
                      (cliente.saldo ?? 0) > 0 ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {formatCurrency(Math.abs(cliente.saldo ?? 0))}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {(cliente.saldo ?? 0) > 0 ? 'a cobrar' : (cliente.saldo ?? 0) < 0 ? 'a favor' : 'sin saldo'}
                    </p>
                  </div>
                  )}
                </div>

                {/* ORR-364 — las etiquetas van en Resumen y no en Fiscal: no son un dato
                    impositivo, son la marca con la que una tarea repetitiva alcanza a un grupo. */}
                {cliente.id && (
                  <EtiquetasClienteCard clienteId={cliente.id} clienteNombre={cliente.nombre ?? ''} />
                )}

                {/* Opt-out del bloque de Instagram en los mails (plan-promo-instagram-mails, Ley 25.326). */}
                {cliente.id && <PromoInstagramClienteCard clienteId={cliente.id} />}

                {/* Preview de servicios */}
                {puede.verHonorariosCliente && serviciosConPrecio.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2 text-primary">
                      <Briefcase className="h-4 w-4" />
                      Servicios Contratados
                    </h3>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {serviciosConPrecio.slice(0, 4).map((s) => (
                        <Badge key={s.id} variant="secondary">
                          {s.servicio_nombre}
                        </Badge>
                      ))}
                      {serviciosConPrecio.length > 4 && (
                        <Badge variant="outline">+{serviciosConPrecio.length - 4} más</Badge>
                      )}
                    </div>
                    <Button
                      variant="link"
                      className="p-0 h-auto text-sm"
                      onClick={() => setActiveTab('servicios')}
                    >
                      Ver detalle <ArrowRight className="h-3 w-3 ml-1" />
                    </Button>
                  </div>
                )}

                {/* Información de registro (auditoría) */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2 text-primary">
                    <Clock className="h-4 w-4" />
                    Información de Registro
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm bg-muted/30 rounded-lg p-4">
                    <div>
                      <p className="text-muted-foreground text-xs uppercase mb-1">Cliente desde</p>
                      <p className="font-medium">
                        {clienteCompleto?.fecha_alta ? formatDate(clienteCompleto.fecha_alta) : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs uppercase mb-1">Responsable</p>
                      <p className="font-medium">{cliente.responsable || '-'}</p>
                    </div>
                    {/* ORR-404: motivo y fecha de la baja. Los clientes que ya estaban
                        inactivos no tienen motivo y nunca lo van a tener. */}
                    {!esActivo && (
                      <>
                        <div>
                          <p className="text-muted-foreground text-xs uppercase mb-1">Baja</p>
                          <p className="font-medium">
                            {clienteCompleto?.fecha_baja ? formatDate(clienteCompleto.fecha_baja) : '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-xs uppercase mb-1">Motivo de la baja</p>
                          <p className="font-medium">
                            {labelMotivoBaja(clienteCompleto?.motivo_baja) ?? (
                              <span className="text-muted-foreground font-normal">
                                Sin motivo registrado
                              </span>
                            )}
                          </p>
                          {clienteCompleto?.motivo_baja_detalle && (
                            <p className="text-muted-foreground text-xs mt-1">
                              {clienteCompleto.motivo_baja_detalle}
                            </p>
                          )}
                        </div>
                      </>
                    )}
                    <div>
                      <p className="text-muted-foreground text-xs uppercase mb-1">Registrado por</p>
                      <p className="font-medium">
                        {persona?.created_by ? persona.created_by.split('@')[0] : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs uppercase mb-1">Última actualización</p>
                      <p className="font-medium">
                        {persona?.updated_at ? formatDate(persona.updated_at) : '-'}
                      </p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* TAB CUENTA CORRIENTE (ORR-141). Sale de movimientos: verMovimientos (ORR-613) */}
              {puede.verMovimientos && (
              <TabsContent value="cuenta" className="p-0">
                <CuentaCorrienteTab
                  cliente={cliente}
                  onEnviarEstadoCuenta={() => onEnviarEstadoCuenta?.(cliente)}
                  onVerVencimientos={() => setActiveTab('vencimientos')}
                />
              </TabsContent>
              )}

              {/* TAB IDENTIDAD */}
              <TabsContent value="identidad" className="px-6 py-4 space-y-6">
                {/* Datos de contacto */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2 text-primary">
                    <User className="h-4 w-4" />
                    Datos de Contacto
                  </h3>
                  <div className="space-y-3">
                    {/* CUIT */}
                    {cliente.cuit && (
                      <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                        <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-muted-foreground uppercase">CUIT</p>
                          <CopyableCell
                            value={cliente.cuit.toString()}
                            displayValue={formatCuit(cliente.cuit)}
                            className="font-medium"
                          />
                        </div>
                      </div>
                    )}

                    {/* Nombre de Fantasía (solo para persona jurídica) */}
                    {persona?.tipo === 'juridica' && persona?.nombre_fantasia && (
                      <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                        <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-muted-foreground uppercase">Nombre de Fantasía</p>
                          <p className="font-medium">{persona.nombre_fantasia}</p>
                        </div>
                      </div>
                    )}

                    {/* Email */}
                    {cliente.email && (
                      <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                        <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-muted-foreground uppercase">Email</p>
                          <CopyableCell
                            value={cliente.email}
                            className="font-medium text-primary"
                          />
                        </div>
                      </div>
                    )}

                    {/* Teléfono */}
                    {cliente.telefono && (
                      <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                        <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-muted-foreground uppercase">Teléfono</p>
                          <div className="flex items-center gap-2">
                            <CopyableCell
                              value={cliente.telefono}
                              className="font-medium"
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-green-600"
                              onClick={() => {
                                const url = generarEnlaceWhatsApp(cliente.telefono)
                                if (url) window.open(url, '_blank')
                              }}
                            >
                              <MessageCircle className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Domicilio */}
                {tieneDomicilio && (
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2 text-primary">
                      <MapPin className="h-4 w-4" />
                      Domicilio
                    </h3>
                    <div className="p-4 bg-muted/30 rounded-lg space-y-1">
                      {domicilioCalle && (
                        <p className="font-medium">{domicilioCalle}</p>
                      )}
                      {domicilioLinea2 && (
                        <p className="text-sm text-muted-foreground">{domicilioLinea2}</p>
                      )}
                      {domicilioLinea3 && (
                        <p className="text-sm text-muted-foreground">{domicilioLinea3}</p>
                      )}
                      {persona?.domicilio_provincia && (
                        <p className="text-sm text-muted-foreground">
                          {persona.domicilio_provincia}, Argentina
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Administrador (solo sucesión indivisa) */}
                {persona?.tipo === 'sucesion_indivisa' && (persona?.admin_nombre || persona?.admin_cuit) && (
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2 text-primary">
                      <User className="h-4 w-4" />
                      Administrador de la Sucesión
                    </h3>
                    <div className="p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        {persona.admin_nombre && (
                          <div>
                            <p className="text-muted-foreground text-xs uppercase mb-1">Nombre</p>
                            <p className="font-medium">{persona.admin_nombre}</p>
                          </div>
                        )}
                        {persona.admin_cuit && (
                          <div>
                            <p className="text-muted-foreground text-xs uppercase mb-1">CUIT</p>
                            <p className="font-medium">{persona.admin_cuit}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Datos de Inscripción (solo para sociedades) */}
                {persona?.tipo === 'juridica' && (
                  faceta?.inscripcion_numero ||
                  faceta?.inscripcion_fojas ||
                  faceta?.inscripcion_tomo ||
                  faceta?.inscripcion_protocolo ||
                  faceta?.inscripcion_fecha ||
                  faceta?.inscripcion_ano
                ) && (
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2 text-primary">
                      <FileText className="h-4 w-4" />
                      Datos de Inscripción
                    </h3>
                    <div className="grid grid-cols-2 gap-4 text-sm bg-muted/30 rounded-lg p-4">
                      {faceta?.inscripcion_numero && (
                        <div>
                          <p className="text-muted-foreground text-xs uppercase mb-1">Número</p>
                          <p className="font-medium">{faceta.inscripcion_numero}</p>
                        </div>
                      )}
                      {faceta?.inscripcion_fojas && (
                        <div>
                          <p className="text-muted-foreground text-xs uppercase mb-1">Fojas</p>
                          <p className="font-medium">{faceta.inscripcion_fojas}</p>
                        </div>
                      )}
                      {faceta?.inscripcion_tomo && (
                        <div>
                          <p className="text-muted-foreground text-xs uppercase mb-1">Tomo</p>
                          <p className="font-medium">{faceta.inscripcion_tomo}</p>
                        </div>
                      )}
                      {faceta?.inscripcion_protocolo && (
                        <div>
                          <p className="text-muted-foreground text-xs uppercase mb-1">Protocolo</p>
                          <p className="font-medium">{faceta.inscripcion_protocolo}</p>
                        </div>
                      )}
                      {faceta?.inscripcion_fecha && (
                        <div>
                          <p className="text-muted-foreground text-xs uppercase mb-1">Fecha de Inscripción</p>
                          <p className="font-medium">
                            {new Date(faceta.inscripcion_fecha).toLocaleDateString('es-AR')}
                          </p>
                        </div>
                      )}
                      {faceta?.inscripcion_ano && (
                        <div>
                          <p className="text-muted-foreground text-xs uppercase mb-1">Año</p>
                          <p className="font-medium">{faceta.inscripcion_ano}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* TAB FISCAL */}
              <TabsContent value="fiscal" className="px-6 py-4 space-y-6">
                {/* Situación tributaria */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2 text-primary">
                    <Building2 className="h-4 w-4" />
                    Situación Tributaria
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm bg-muted/30 rounded-lg p-4">
                    {faceta?.condicion_iva && (
                      <div>
                        <p className="text-muted-foreground text-xs uppercase mb-1">Condición ARCA</p>
                        <p className="font-medium">{formatCondicionIVA(faceta.condicion_iva)}</p>
                      </div>
                    )}
                    {faceta?.categoria_monotributo && (
                      <div>
                        <p className="text-muted-foreground text-xs uppercase mb-1">Categoría</p>
                        <p className="font-medium">Monotributo {faceta.categoria_monotributo}</p>
                      </div>
                    )}
                    {faceta?.regimen_iibb && (
                      <div>
                        <p className="text-muted-foreground text-xs uppercase mb-1">Régimen IIBB</p>
                        <p className="font-medium">
                          {faceta.regimen_iibb === 'local'
                            ? `Local (${faceta.provincia_principal || persona?.domicilio_provincia || '-'})`
                            : faceta.regimen_iibb === 'convenio_multilateral'
                              ? 'Convenio Multilateral'
                              : faceta.regimen_iibb === 'simplificado'
                                ? 'Simplificado (Monotributo)'
                                : faceta.regimen_iibb === 'no_gravado'
                                  ? 'No Gravado'
                                  : faceta.regimen_iibb}
                        </p>
                      </div>
                    )}
                    {persona?.tipo === 'juridica' && faceta?.cierre_ejercicio && (
                      <div>
                        <p className="text-muted-foreground text-xs uppercase mb-1">Cierre Ejercicio</p>
                        <p className="font-medium">{faceta.cierre_ejercicio}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actividades económicas */}
                {actividades.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2 text-primary">
                      <Briefcase className="h-4 w-4" />
                      Actividades Económicas
                    </h3>
                    <div className="space-y-3">
                      {actividades.map((actividad, index) => (
                        <div key={actividad.codigo || index} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                          <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium shrink-0 ${
                            index === 0
                              ? 'bg-primary/20 text-primary'
                              : 'bg-muted text-muted-foreground'
                          }`}>
                            {index + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-muted-foreground uppercase mb-0.5">
                              {index === 0 ? 'Principal' : 'Secundaria'}
                            </p>
                            <p className="font-medium text-sm">
                              <span className="font-mono text-muted-foreground mr-2 text-xs">
                                {actividad.codigo}
                              </span>
                              {actividad.descripcion}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Impuestos a liquidar */}
                {impuestosCliente.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2 text-primary">
                      <FileText className="h-4 w-4" />
                      Impuestos a Liquidar
                    </h3>
                    <div className="space-y-2">
                      {impuestosCliente.map((ic) => (
                        <div
                          key={ic.id}
                          className="flex items-center gap-3 p-2.5 bg-muted/30 rounded-lg"
                        >
                          <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                          <ImpuestoBadge
                            nombre={ic.impuesto.nombre_corto || ic.impuesto.nombre}
                            color={(ic.impuesto.color || 'gray') as ColorImpuesto}
                            size="sm"
                          />
                          <div className="flex-1 min-w-0">
                            <span className="text-sm text-muted-foreground truncate block">
                              {ic.impuesto.nombre}
                            </span>
                            {(ic.numero_inscripcion || ic.alicuota) && (
                              <span className="text-xs text-muted-foreground">
                                {ic.numero_inscripcion && `Nro. ${ic.numero_inscripcion}`}
                                {ic.numero_inscripcion && ic.alicuota && ' · '}
                                {ic.alicuota && `${ic.alicuota}%`}
                              </span>
                            )}
                            {ic.fecha_inscripcion && (
                              <span className="text-xs text-muted-foreground block">
                                Inscripto: {ic.fecha_inscripcion.slice(5, 7)}/{ic.fecha_inscripcion.slice(0, 4)} (ARCA)
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bloque Domicilio fiscal */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2 text-primary">
                    <MapPin className="h-4 w-4" />
                    Domicilio fiscal
                  </h3>
                  {tieneDomicilioCargado ? (
                    <div className="text-sm space-y-1">
                      {domicilioCalle && <p>{domicilioCalle}</p>}
                      {(persona?.domicilio_localidad || persona?.domicilio_provincia || persona?.domicilio_cp) && (
                        <p className="text-muted-foreground">
                          {[persona?.domicilio_localidad, persona?.domicilio_provincia, persona?.domicilio_cp]
                            .filter(Boolean)
                            .join(', ')}
                        </p>
                      )}
                      {persona?.padron_sincronizado_en && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Última sincronización con AFIP: {format(parseISO(persona.padron_sincronizado_en), 'dd/MM/yyyy HH:mm')}
                          {' '}({formatDistanceToNow(parseISO(persona.padron_sincronizado_en), { addSuffix: true, locale: es })})
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Sin datos cargados.</p>
                  )}
                  {puede.sincronizarPadron && cuitString && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleSincronizar}
                      disabled={sincronizarPadron.isPending || previewLoading}
                      className="mt-2 gap-1.5"
                    >
                      {(sincronizarPadron.isPending || previewLoading) ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <RefreshCw className="h-3.5 w-3.5" />
                      )}
                      {sincronizarPadron.isPending || previewLoading ? 'Sincronizando...' : 'Sincronizar desde AFIP'}
                    </Button>
                  )}
                </div>

                {/* Banner delegación ARCA — solo si hay permiso, CUIT y estado != activa */}
                {puede.verCasilleroCliente && cuitString && clienteCompleto?.delegacion_eventanilla && (
                  <DelegacionARCABanner
                    estado={clienteCompleto.delegacion_eventanilla as EstadoDelegacion}
                    cuitCliente={cuitString}
                    nombreCliente={cliente?.nombre ?? clienteCompleto.nombre ?? ''}
                    clienteId={clienteCompleto.id}
                    organizacionId={clienteCompleto.organizacion_id ?? ''}
                  />
                )}

                {/* Acceso directo a AFIP (Letrita A): el botón se muestra si el cliente tiene clave de ARCA guardada */}
                {puede.letritaA && cuitString && clienteCompleto && (
                  <AbrirAfipDirectoButton
                    clienteId={clienteCompleto.id}
                    nombreCliente={cliente?.nombre ?? clienteCompleto.nombre ?? ''}
                  />
                )}

                {/* Bloque Casillero ARCA — solo si tiene permiso */}
                {puede.verCasilleroCliente && (
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2 text-primary">
                      <Inbox className="h-4 w-4" />
                      Casillero electrónico ARCA
                    </h3>
                    {!cuitString ? (
                      <p className="text-sm text-muted-foreground">Cliente sin CUIT registrado.</p>
                    ) : casilleroResumen.isLoading ? (
                      <p className="text-sm text-muted-foreground flex items-center gap-2">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Consultando ARCA...
                      </p>
                    ) : casilleroResumen.error ? (
                      <p className="text-sm text-destructive">{mapearErrorAFIP(casilleroResumen.error)}</p>
                    ) : casilleroResumen.data ? (
                      <div className="text-sm space-y-2">
                        {casilleroResumen.data.noLeidas > 0 ? (
                          <p className="font-medium text-amber-600">
                            ⚠️ {casilleroResumen.data.noLeidas} mensaje{casilleroResumen.data.noLeidas > 1 ? 's' : ''} sin leer
                          </p>
                        ) : (
                          <p className="text-muted-foreground">Sin mensajes nuevos</p>
                        )}
                        {casilleroResumen.data.ultima && (
                          <p className="text-muted-foreground text-xs">
                            Último: <strong>{casilleroResumen.data.ultima.asunto}</strong> ·{' '}
                            {format(parseISO(casilleroResumen.data.ultima.fechaEmision), 'dd/MM/yyyy')}
                          </p>
                        )}
                        <Link to={`/fiscal/casillero?cuit=${cuitString}`}>
                          <Button size="sm" variant="outline" className="gap-1.5">
                            Ver comunicaciones
                            <ArrowRight className="h-3.5 w-3.5" />
                          </Button>
                        </Link>
                      </div>
                    ) : null}
                  </div>
                )}

                {/* Estado vacío */}
                {!faceta?.condicion_iva && actividades.length === 0 && impuestosCliente.length === 0 && !tieneDomicilioCargado && !cuitString && (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="h-10 w-10 mx-auto mb-3 opacity-30" />
                    <p>No hay información fiscal configurada</p>
                  </div>
                )}
              </TabsContent>

              {/* TAB SERVICIOS */}
              {puede.verHonorariosCliente && (
              <TabsContent value="servicios" className="px-6 py-4 space-y-6">
                {serviciosConPrecio.length > 0 ? (
                  <>
                    <div>
                      <h3 className="font-semibold mb-3 flex items-center gap-2 text-primary">
                        <Receipt className="h-4 w-4" />
                        Honorarios Mensuales
                      </h3>
                      <div className="space-y-3">
                        {serviciosConPrecio.map((servicio) => {
                          const equivalenteMensual = calcularEquivalenteMensual(
                            servicio.precio,
                            servicio.frecuencia || 'mensual'
                          )
                          const noEsMensual = servicio.frecuencia && servicio.frecuencia !== 'mensual'

                          return (
                            <div
                              key={servicio.id}
                              className="p-4 border rounded-lg bg-card hover:border-primary/30 transition-colors"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium truncate">{servicio.servicio_nombre}</p>
                                  <p className="text-sm text-muted-foreground mt-1">
                                    {formatCurrency(servicio.precio)} · {formatFrecuencia(servicio.frecuencia || 'mensual')}
                                  </p>
                                </div>
                                <div className="text-right shrink-0">
                                  <p className="text-lg font-bold text-primary">
                                    {formatCurrency(equivalenteMensual)}
                                  </p>
                                  {noEsMensual && (
                                    <p className="text-xs text-muted-foreground">equiv. mensual</p>
                                  )}
                                </div>
                              </div>
                              {servicio.fecha_desde && (
                                <div className="flex items-center gap-1.5 mt-3 pt-3 border-t text-xs text-muted-foreground">
                                  <Calendar className="h-3 w-3" />
                                  Desde {formatDate(servicio.fecha_desde)}
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>

                    {/* Total mensual */}
                    <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">Total Mensual Estimado</span>
                        <span className="text-xl font-bold text-primary">
                          {formatCurrency(totalMensualServicios)}
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  /* Estado vacío */
                  <div className="text-center py-12 text-muted-foreground">
                    <Receipt className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p className="font-medium mb-1">Sin honorarios configurados</p>
                    <p className="text-sm">
                      Los servicios se pueden agregar editando el cliente.
                    </p>
                  </div>
                )}
              </TabsContent>
              )}

              {/* TAB VENCIMIENTOS */}
              <TabsContent value="vencimientos" className="px-6 py-4 space-y-4">
                {loadingVencimientos ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />
                    ))}
                  </div>
                ) : vencimientosCliente.length > 0 ? (
                  <>
                    {/* Stats resumen */}
                    <div className="grid grid-cols-3 gap-2">
                      {(['pendiente', 'presentado', 'vencido'] as const).map((estado) => {
                        const config = ESTADOS_VENCIMIENTO_FISCAL[estado]
                        const count = vencimientosCliente.filter(
                          (v) => (v.estado_efectivo || v.estado) === estado
                        ).length
                        return (
                          <div key={estado} className={`rounded-lg p-2 text-center ${config.bgColor}`}>
                            <p className={`text-lg font-bold ${config.color}`}>{count}</p>
                            <p className="text-xs text-muted-foreground">{config.label}</p>
                          </div>
                        )
                      })}
                    </div>

                    {/* Reparto de conceptos entre cualquiera del estudio (ORR-230, ampliado en ORR-303) */}
                    <RepartoConceptos
                      clienteId={cliente.id ?? ''}
                      vencimientos={vencimientosCliente}
                    />

                    {/* Botón descargar PDF */}
                    <div className="flex justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={generandoPDF}
                        onClick={() => {
                          if (!clienteCompleto) return
                          descargarPDF(
                            {
                              nombre: clienteCompleto.nombre ?? '',
                              cuit: clienteCompleto.cuit ?? undefined,
                              condicionIva: clienteCompleto.condicion_iva ?? undefined,
                            },
                            vencimientosCliente,
                          )
                        }}
                      >
                        {generandoPDF ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4 mr-1" />
                        )}
                        Descargar PDF
                      </Button>
                    </div>

                    {/* Lista de vencimientos */}
                    <div className="space-y-2">
                      {vencimientosCerrados.length > 0 && (
                        <div className="flex justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setVerVencimientosCerrados((v) => !v)}
                          >
                            {verVencimientosCerrados
                              ? 'Ocultar presentados'
                              : `Ver presentados (${vencimientosCerrados.length})`}
                          </Button>
                        </div>
                      )}
                      {vencimientosVisibles.length === 0 && (
                        <p className="text-sm text-center text-muted-foreground py-4">
                          No hay vencimientos pendientes.
                        </p>
                      )}
                      {vencimientosVisibles
                        .slice(0, 20)
                        .map((v) => {
                          const estado = estadoVencimiento(v) as keyof typeof ESTADOS_VENCIMIENTO_FISCAL
                          const estadoConfig = ESTADOS_VENCIMIENTO_FISCAL[estado] ?? ESTADOS_VENCIMIENTO_FISCAL.pendiente
                          return (
                            <div
                              key={v.id}
                              className="flex items-center justify-between rounded-lg border p-3"
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium truncate">
                                    {v.tipo_impuesto}
                                  </span>
                                  <Badge
                                    variant="outline"
                                    className={`text-xs ${estadoConfig.color} ${estadoConfig.bgColor}`}
                                  >
                                    {estadoConfig.label}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                                  <Calendar className="h-3 w-3" />
                                  <span>{v.fecha_vencimiento ? formatDate(v.fecha_vencimiento) : 'Sin fecha'}</span>
                                  <span className="mx-1">|</span>
                                  <span>{v.periodo}</span>
                                </div>
                              </div>
                              {v.monto_determinado != null && Number(v.monto_determinado) > 0 && (
                                <span className="text-sm font-medium text-right">
                                  {formatCurrency(Number(v.monto_determinado))}
                                </span>
                              )}
                            </div>
                          )
                        })}
                      {vencimientosVisibles.length > 20 && (
                        <p className="text-xs text-center text-muted-foreground pt-2">
                          Mostrando 20 de {vencimientosVisibles.length} vencimientos
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Calendar className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p className="font-medium mb-1">Sin vencimientos</p>
                    <p className="text-sm">
                      No hay vencimientos registrados para este cliente.
                    </p>
                  </div>
                )}
              </TabsContent>

              {/* TAB CLAVES */}
              <TabsContent value="claves" className="px-6 py-4">
                {cliente?.id && (
                  <CajaFuertePanel
                    clienteId={cliente.id}
                    cuit={cliente.cuit != null ? String(cliente.cuit) : null}
                  />
                )}
              </TabsContent>

              {/* TAB TAREAS (ORR-327) — las no completadas del cliente, y las completadas a pedido.
                  Gateado por permiso: RLS no distingue roles, así que esconder el bloque
                  no alcanza; sin `verTareas` el componente no se monta y la query no corre. */}
              {puede.verTareas && (
                <TabsContent value="tareas" className="px-6 py-4 space-y-3">
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setVerTareasCompletadas((v) => !v)}
                    >
                      {verTareasCompletadas ? 'Ocultar completadas' : 'Ver completadas'}
                    </Button>
                  </div>
                  {cliente?.id && (
                    <TareasLista
                      filtroCliente={cliente.id}
                      clavePreferencias="tareas-lista-cliente"
                      mostrarCompletadas={verTareasCompletadas}
                      canEdit={false}
                      canDelete={false}
                      // La lista de un cliente trae tareas de todo el equipo, así que el
                      // cambio de estado se gatea por fila: con solo `editarTareaPropia`
                      // la RPC rechaza las ajenas y el menú prometía algo que se revierte.
                      canChangeEstado={puedeCambiarEstadoTarea}
                      onVerDetalle={setTareaDetalle}
                    />
                  )}
                </TabsContent>
              )}
            </Tabs>
          )}
        </div>
      </SheetContent>

      {/* AlertDialog confirmación inteligente — diff Antes/Después de sincronizar AFIP */}
      <AlertDialog open={diffDialogOpen} onOpenChange={setDiffDialogOpen}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar sincronización con AFIP</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                {diffRows.length === 0 ? (
                  <p>El padrón de AFIP coincide con los datos cargados. No hay cambios para aplicar.</p>
                ) : (
                  <>
                    <p>Esto reemplazará los siguientes datos con lo que figura en ARCA:</p>
                    <table className="w-full text-xs border border-border">
                      <thead className="bg-muted">
                        <tr>
                          <th className="p-2 text-left font-medium">Campo</th>
                          <th className="p-2 text-left font-medium">Antes</th>
                          <th className="p-2 text-left font-medium">Después</th>
                        </tr>
                      </thead>
                      <tbody>
                        {diffRows.map((r) => (
                          <tr key={r.field} className="border-t border-border">
                            <td className="p-2">{r.label}</td>
                            <td className="p-2 line-through text-muted-foreground">{r.before || '—'}</td>
                            <td className="p-2 font-medium">{r.after || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            {diffRows.length > 0 && (
              <AlertDialogAction onClick={confirmarSincronizar}>Reemplazar</AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Detalle de una tarea de la solapa. Es otro Sheet sobre este: Radix apila los
          layers, asi que Escape y el click afuera cierran solo el de arriba. Sin
          `onEditar` — la ficha del cliente es de consulta. */}
      <TareaDetalleDialog
        open={tareaDetalle !== null}
        onOpenChange={(abierto) => {
          if (!abierto) setTareaDetalle(null)
        }}
        tarea={tareaDetalle}
        canChangeEstado={tareaDetalle !== null && puedeCambiarEstadoTarea(tareaDetalle)}
      />
    </Sheet>
  )
}

function formatCuit(cuit: number): string {
  const str = cuit.toString().padStart(11, '0')
  return `${str.slice(0, 2)}-${str.slice(2, 10)}-${str.slice(10)}`
}

function formatCondicionIVA(condicion: string): string {
  const labels: Record<string, string> = {
    responsable_inscripto: 'Responsable Inscripto',
    monotributo: 'Monotributista',
    monotributo_social: 'Monotributo Social',
    exento: 'Exento',
    consumidor_final: 'Consumidor Final',
    no_responsable: 'No Responsable',
  }
  return labels[condicion] || condicion
}

function formatTipoPersona(tipo: string): string {
  const labels: Record<string, string> = {
    fisica: 'Persona Física',
    juridica: 'Sociedad',
    sucesion_indivisa: 'Sucesión Indivisa',
  }
  return labels[tipo] || tipo
}
